/**
 * Pure schedule window matchers (business timezone via dayjs).
 * Shared by main API and call-service-api. Free of DB imports for unit testing.
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezonePlugin from 'dayjs/plugin/timezone.js';
const DEFAULT_TIMEZONE = 'America/Chicago';
dayjs.extend(utc);
dayjs.extend(timezonePlugin);
export function timeToMinutes(timeStr) {
    const [h, m] = timeStr.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
}
function localWallClock(at, timeZone) {
    const zone = timeZone || DEFAULT_TIMEZONE;
    const local = dayjs(at).tz(zone);
    return {
        dayOfWeek: local.day(),
        minutes: local.hour() * 60 + local.minute(),
        dateStr: local.format('YYYY-MM-DD'),
    };
}
export function isWithinDailySchedule(at, config, timeZone = DEFAULT_TIMEZONE) {
    const dayOfWeek = config.dayOfWeek;
    const startTime = config.startTime || '00:00';
    const endTime = config.endTime || '23:59';
    if (dayOfWeek === undefined)
        return false;
    const { dayOfWeek: currentDay, minutes: currentMinutes } = localWallClock(at, timeZone);
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    if (startMinutes <= endMinutes) {
        return currentDay === dayOfWeek && currentMinutes >= startMinutes && currentMinutes < endMinutes;
    }
    // Overnight e.g. Monday 18:00–08:00 → Monday after 18:00 through Tuesday before 08:00
    if (currentDay === dayOfWeek) {
        return currentMinutes >= startMinutes;
    }
    const nextDay = (dayOfWeek + 1) % 7;
    if (currentDay === nextDay) {
        return currentMinutes < endMinutes;
    }
    return false;
}
export function isWithinWeeklySchedule(at, config, timeZone = DEFAULT_TIMEZONE) {
    const startDate = config.startDate;
    const endDate = config.endDate;
    const startTime = config.startTime || '00:00';
    const endTime = config.endTime || '23:59';
    if (!startDate || !endDate)
        return false;
    const { dateStr, minutes: currentMinutes } = localWallClock(at, timeZone);
    if (dateStr < startDate || dateStr > endDate)
        return false;
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    if (startMinutes <= endMinutes) {
        return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    }
    return currentMinutes >= startMinutes || currentMinutes < endMinutes;
}
export function isWithinSpecificBlock(at, config) {
    const blocks = config.blocks;
    if (!blocks || !Array.isArray(blocks))
        return false;
    const atMs = at.getTime();
    for (const block of blocks) {
        const start = new Date(block.start).getTime();
        const end = new Date(block.end).getTime();
        if (Number.isNaN(start) || Number.isNaN(end))
            continue;
        if (atMs >= start && atMs < end)
            return true;
    }
    return false;
}
/**
 * True when any HH:mm split-shift block on the given calendar day contains `at`.
 * Used when config.blocks are wall-clock times (not absolute ISO).
 */
export function isWithinSplitShiftBlocks(at, config, timeZone = DEFAULT_TIMEZONE) {
    const blocks = config.blocks;
    if (!blocks?.length)
        return false;
    const { dayOfWeek: currentDay, minutes: currentMinutes, dateStr } = localWallClock(at, timeZone);
    if (config.dayOfWeek !== undefined && currentDay !== config.dayOfWeek) {
        return false;
    }
    if (config.startDate && dateStr < config.startDate)
        return false;
    if (config.endDate && dateStr > config.endDate)
        return false;
    for (const block of blocks) {
        // Absolute ISO blocks
        if (block.start.includes('T') || block.end.includes('T')) {
            const start = new Date(block.start).getTime();
            const end = new Date(block.end).getTime();
            if (!Number.isNaN(start) && !Number.isNaN(end) && at.getTime() >= start && at.getTime() < end) {
                return true;
            }
            continue;
        }
        const startMinutes = timeToMinutes(block.start);
        const endMinutes = timeToMinutes(block.end);
        if (startMinutes <= endMinutes) {
            if (currentMinutes >= startMinutes && currentMinutes < endMinutes)
                return true;
        }
        else if (currentMinutes >= startMinutes || currentMinutes < endMinutes) {
            return true;
        }
    }
    return false;
}
export function scheduleMatchesAt(scheduleType, config, at, timeZone) {
    switch (scheduleType) {
        case 'daily':
            if (config.blocks?.length && !config.blocks[0]?.start?.includes('T')) {
                return isWithinSplitShiftBlocks(at, config, timeZone);
            }
            return isWithinDailySchedule(at, config, timeZone);
        case 'weekly':
            return isWithinWeeklySchedule(at, config, timeZone);
        case 'specific':
            return isWithinSpecificBlock(at, config);
        default:
            return false;
    }
}
export const DEFAULT_SCHEDULE_TIMEZONE = DEFAULT_TIMEZONE;
//# sourceMappingURL=scheduleWindowMatch.js.map